/**
 * Vehicle mesh factory: one cached template per VehicleType (merged geometries + shared
 * materials), cloned per instance by vehicle.ts.  Zero external assets: canvas textures only.
 *
 * Local space of a vehicle: +Z forward, +Y up, +X = LEFT side (driver door in Hungary).
 * Every part is merged by material so a car costs ~9 draw calls (body, trim, glass, head,
 * tail, 4 wheels) plus type extras (light bar, taxi sign, cargo box, livery panels).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { VehicleType } from './types';

// ------------------------------------------------------------------ palette ----

/** 14 tasteful body colours (dark metallics, whites, silver, a few saturated). */
export const CAR_COLORS: readonly number[] = [
  0x1b1f26, // gunmetal
  0x2a2f3a, // charcoal
  0x0f0f12, // black
  0xf1efe9, // pearl white
  0xc8cbd0, // silver
  0x6f7580, // grey
  0x1c2a52, // navy
  0x3b5f8f, // steel blue
  0x1e4d3b, // racing green
  0x7a1f26, // burgundy
  0xb8322a, // red
  0xd07a1f, // orange
  0x8c6a3f, // bronze
  0x5b3d7a, // plum
];
export const TAXI_YELLOW = 0xf2c318;
export const POLICE_WHITE = 0xe9ecef;
export const BUS_BLUE = 0x2f6fbd;

// ------------------------------------------------------------------- types ----

export interface WheelSlot {
  x: number;
  z: number;
  steer: boolean;
}
export interface SeatSlot {
  x: number;
  y: number;
  z: number;
}
export type ExtraKind = 'static' | 'taxiSign' | 'lensRed' | 'lensBlue';
export interface ExtraPart {
  geo: THREE.BufferGeometry;
  mat: THREE.Material;
  kind: ExtraKind;
  castShadow: boolean;
}

export interface VehicleTemplate {
  type: VehicleType;
  body: THREE.BufferGeometry;
  trim: THREE.BufferGeometry;
  glass: THREE.BufferGeometry;
  head: THREE.BufferGeometry;
  tail: THREE.BufferGeometry;
  extras: ExtraPart[];
  wheels: WheelSlot[];
  wheelRadius: number;
  wheelGeo: THREE.BufferGeometry;
  seats: SeatSlot[];
  /** z of the driver door (door position is at x = width/2 + 0.9, this z) */
  doorZ: number;
  /** where damage smoke / fire come from */
  bonnet: { y: number; z: number };
  wheelbase: number;
  /** collision circle centres along local z, all with radius colR */
  colZ: number[];
  colR: number;
  /** default body colour for the type when none is given (null = pick from palette) */
  defaultColor: number | null;
  /** headlight anchor (per side, x is mirrored) for the player's real spot lights */
  headlightPos: { x: number; y: number; z: number };
}

// ------------------------------------------------------------ materials ----

interface SharedMats {
  trim: THREE.MeshStandardMaterial;
  glass: THREE.MeshStandardMaterial;
  cargo: THREE.MeshStandardMaterial;
  cream: THREE.MeshStandardMaterial;
  policeBlue: THREE.MeshStandardMaterial;
  wheel: THREE.MeshStandardMaterial;
  plate: THREE.MeshStandardMaterial;
  policeDoor: THREE.MeshStandardMaterial;
  taxiCheck: THREE.MeshStandardMaterial;
  busSign: THREE.MeshStandardMaterial;
  /** templates cloned per instance */
  body: THREE.MeshStandardMaterial;
  head: THREE.MeshStandardMaterial;
  tail: THREE.MeshStandardMaterial;
  lensRed: THREE.MeshStandardMaterial;
  lensBlue: THREE.MeshStandardMaterial;
  taxiSign: THREE.MeshStandardMaterial;
}

let mats: SharedMats | null = null;

function canvasTexture(w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  draw(ctx);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  return t;
}

const FONT_BOLD = '"Bebas", "Impact", "Arial Narrow", "Arial Black", sans-serif';

function wheelTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 256, (ctx) => {
    // ---- left half: wheel face (tyre sidewall + rim with 5 spokes)
    const cx = 128, cy = 128;
    ctx.fillStyle = '#121212';
    ctx.fillRect(0, 0, 256, 256);
    // sidewall ring
    const sw = ctx.createRadialGradient(cx, cy, 90, cx, cy, 128);
    sw.addColorStop(0, '#242424');
    sw.addColorStop(0.55, '#1a1a1a');
    sw.addColorStop(1, '#0e0e0e');
    ctx.fillStyle = sw;
    ctx.beginPath();
    ctx.arc(cx, cy, 128, 0, Math.PI * 2);
    ctx.fill();
    // rim lip
    ctx.fillStyle = '#d4d7dc';
    ctx.beginPath();
    ctx.arc(cx, cy, 92, 0, Math.PI * 2);
    ctx.fill();
    // rim dish (dark between spokes)
    const dish = ctx.createRadialGradient(cx, cy, 10, cx, cy, 90);
    dish.addColorStop(0, '#4a4d54');
    dish.addColorStop(1, '#2a2d33');
    ctx.fillStyle = dish;
    ctx.beginPath();
    ctx.arc(cx, cy, 86, 0, Math.PI * 2);
    ctx.fill();
    // spokes
    ctx.save();
    ctx.translate(cx, cy);
    for (let i = 0; i < 5; i++) {
      ctx.save();
      ctx.rotate((i / 5) * Math.PI * 2);
      const g = ctx.createLinearGradient(-14, 0, 14, 0);
      g.addColorStop(0, '#9da1a8');
      g.addColorStop(0.5, '#d9dce0');
      g.addColorStop(1, '#8e9299');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(-9, -18);
      ctx.lineTo(9, -18);
      ctx.lineTo(15, -86);
      ctx.lineTo(-15, -86);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
    // hub
    ctx.fillStyle = '#c3c6cb';
    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#3a3d43';
    ctx.beginPath();
    ctx.arc(cx, cy, 8, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + 0.3;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(a) * 15, cy + Math.sin(a) * 15, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    // ---- right half: tread
    ctx.fillStyle = '#1b1b1b';
    ctx.fillRect(256, 0, 256, 256);
    ctx.fillStyle = '#242424';
    for (let x = 256; x < 512; x += 12) ctx.fillRect(x, 0, 5, 256);
    ctx.fillStyle = '#101010';
    ctx.fillRect(256, 86, 256, 4);
    ctx.fillRect(256, 166, 256, 4);
  });
}

function plateTexture(): THREE.CanvasTexture {
  return canvasTexture(256, 64, (ctx) => {
    ctx.fillStyle = '#f4f4f0';
    ctx.fillRect(0, 0, 256, 64);
    ctx.fillStyle = '#1f4fa8';
    ctx.fillRect(0, 0, 30, 64);
    ctx.fillStyle = '#ffd34d';
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(15 + Math.cos(a) * 8, 22 + Math.sin(a) * 8, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 11px ${FONT_BOLD}`;
    ctx.textAlign = 'center';
    ctx.fillText('H', 15, 50);
    ctx.fillStyle = '#1a1a1a';
    ctx.font = `bold 46px ${FONT_BOLD}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('GTB 420', 143, 34);
  });
}

function policeDoorTexture(): THREE.CanvasTexture {
  return canvasTexture(1024, 200, (ctx) => {
    ctx.fillStyle = '#1e4ea8';
    ctx.fillRect(0, 0, 1024, 200);
    ctx.fillStyle = '#f4f6f8';
    ctx.fillRect(0, 0, 1024, 10);
    ctx.fillRect(0, 190, 1024, 10);
    ctx.font = `bold 128px ${FONT_BOLD}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('POLICE', 512, 92);
    ctx.font = `bold 34px ${FONT_BOLD}`;
    ctx.fillStyle = '#cfe0ff';
    ctx.fillText('BUDAPEST', 512, 168);
  });
}

function taxiCheckTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 32, (ctx) => {
    ctx.fillStyle = '#f2c318';
    ctx.fillRect(0, 0, 512, 32);
    ctx.fillStyle = '#151515';
    for (let x = 0; x < 512; x += 32) {
      ctx.fillRect(x, 0, 16, 16);
      ctx.fillRect(x + 16, 16, 16, 16);
    }
  });
}

function taxiSignTexture(): THREE.CanvasTexture {
  return canvasTexture(256, 96, (ctx) => {
    ctx.fillStyle = '#fbf6e4';
    ctx.fillRect(0, 0, 256, 96);
    ctx.fillStyle = '#151515';
    ctx.font = `bold 78px ${FONT_BOLD}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('TAXI', 128, 52);
  });
}

function busSignTexture(): THREE.CanvasTexture {
  return canvasTexture(512, 80, (ctx) => {
    ctx.fillStyle = '#0a0a0c';
    ctx.fillRect(0, 0, 512, 80);
    ctx.fillStyle = '#ffa63a';
    ctx.font = `bold 58px ${FONT_BOLD}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('7', 24, 42);
    ctx.font = `bold 44px ${FONT_BOLD}`;
    ctx.fillText('CITY CENTRE', 84, 42);
  });
}

function getMats(): SharedMats {
  if (mats) return mats;
  const wheelTex = wheelTexture();
  const busTex = busSignTexture();
  const taxiTex = taxiSignTexture();
  mats = {
    trim: new THREE.MeshStandardMaterial({ color: 0x1a1a1d, roughness: 0.78, metalness: 0.15 }),
    glass: new THREE.MeshStandardMaterial({ color: 0x121c27, roughness: 0.34, metalness: 0.55 }),
    cargo: new THREE.MeshStandardMaterial({ color: 0xd9d9d4, roughness: 0.62, metalness: 0.08 }),
    cream: new THREE.MeshStandardMaterial({ color: 0xf3eedd, roughness: 0.5, metalness: 0.05 }),
    policeBlue: new THREE.MeshStandardMaterial({ color: 0x1e4ea8, roughness: 0.4, metalness: 0.25 }),
    wheel: new THREE.MeshStandardMaterial({ map: wheelTex, roughness: 0.72, metalness: 0.35 }),
    plate: new THREE.MeshStandardMaterial({ map: plateTexture(), roughness: 0.5, metalness: 0.1 }),
    policeDoor: new THREE.MeshStandardMaterial({ map: policeDoorTexture(), roughness: 0.42, metalness: 0.25 }),
    taxiCheck: new THREE.MeshStandardMaterial({ map: taxiCheckTexture(), roughness: 0.45, metalness: 0.2 }),
    busSign: new THREE.MeshStandardMaterial({ map: busTex, emissive: 0xffffff, emissiveMap: busTex, emissiveIntensity: 0.9, roughness: 0.4, metalness: 0.1 }),
    body: new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5, metalness: 0.22 }),
    head: new THREE.MeshStandardMaterial({ color: 0xdfe4ea, emissive: 0xfff3d2, emissiveIntensity: 0, roughness: 0.25, metalness: 0.6 }),
    tail: new THREE.MeshStandardMaterial({ color: 0x7c1319, emissive: 0xff1408, emissiveIntensity: 0, roughness: 0.3, metalness: 0.3 }),
    lensRed: new THREE.MeshStandardMaterial({ color: 0xb01a1a, emissive: 0xff2020, emissiveIntensity: 0.1, roughness: 0.2, metalness: 0.2 }),
    lensBlue: new THREE.MeshStandardMaterial({ color: 0x1a3fb0, emissive: 0x2a5cff, emissiveIntensity: 0.1, roughness: 0.2, metalness: 0.2 }),
    taxiSign: new THREE.MeshStandardMaterial({ map: taxiTex, emissive: 0xffffff, emissiveMap: taxiTex, emissiveIntensity: 0, roughness: 0.5, metalness: 0.05 }),
  };
  return mats;
}

/** shared materials accessor (vehicle.ts clones body/head/tail/lens/taxiSign per instance) */
export function vehicleMats(): SharedMats {
  return getMats();
}

// ------------------------------------------------------- geometry helpers ----

type Pt = [number, number];
const ni = (g: THREE.BufferGeometry): THREE.BufferGeometry => (g.index ? g.toNonIndexed() : g);

function box(w: number, h: number, d: number, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  if (rz) g.rotateZ(rz);
  if (rx) g.rotateX(rx);
  if (ry) g.rotateY(ry);
  g.translate(x, y, z);
  return ni(g);
}

/** BoxGeometry whose faces other than `keep` (indices px,nx,py,ny,pz,nz = 0..5) sample a plain texel. */
function texBox(w: number, h: number, d: number, x: number, y: number, z: number, keep: number[], u = 0.01, v = 0.99): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  const uv = g.attributes.uv as THREE.BufferAttribute;
  for (let f = 0; f < 6; f++) {
    if (keep.includes(f)) continue;
    for (let i = 0; i < 4; i++) uv.setXY(f * 4 + i, u, v);
  }
  g.translate(x, y, z);
  return ni(g);
}

function cyl(r: number, len: number, x: number, y: number, z: number, axis: 'x' | 'y' | 'z', seg = 10): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1);
  if (axis === 'x') g.rotateZ(Math.PI / 2);
  else if (axis === 'z') g.rotateX(Math.PI / 2);
  g.translate(x, y, z);
  return ni(g);
}

/**
 * Side-profile silhouette (points in (z, y)) extruded across the width with a chamfer.
 * The extruded walls sit exactly on the outline; the chamfer eats inward by `bevel`.
 */
function profileGeo(pts: Pt[], width: number, bevel: number, xOffset = 0): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][1]);
  shape.closePath();
  const depth = width - 2 * bevel;
  const g = new THREE.ExtrudeGeometry(shape, {
    depth,
    steps: 1,
    bevelEnabled: bevel > 0,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelOffset: -bevel,
    bevelSegments: 3,
    curveSegments: 4,
  });
  g.rotateY(-Math.PI / 2); // shape x -> world z (forward), extrusion -> world x
  g.translate(depth / 2 + xOffset, 0, 0);
  return ni(g);
}

/** Flat polygon on one side of the car (side +1 = left/+X, -1 = right/-X), points in (z, y). */
function sideQuad(side: 1 | -1, pts: Pt[], x: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(side > 0 ? -pts[0][0] : pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) shape.lineTo(side > 0 ? -pts[i][0] : pts[i][0], pts[i][1]);
  shape.closePath();
  const g = new THREE.ShapeGeometry(shape);
  g.rotateY(side > 0 ? Math.PI / 2 : -Math.PI / 2);
  g.translate(x, 0, 0);
  return ni(g);
}
function sideQuads(pts: Pt[], x: number): THREE.BufferGeometry[] {
  return [sideQuad(1, pts, x), sideQuad(-1, pts, -x)];
}

/**
 * Slanted trapezoid (windscreen / rear window): bottom edge at (zB, yB) width wB, top edge at
 * (zT, yT) width wT. facing +1 = looks forward (+Z), -1 = looks backward. Pushed `offset` out
 * along its normal so it sits just above the body surface.
 */
function slantQuad(zB: number, yB: number, zT: number, yT: number, wB: number, wT: number, facing: 1 | -1, offset = 0.012): THREE.BufferGeometry {
  const zb = facing > 0 ? zB : -zB;
  const zt = facing > 0 ? zT : -zT;
  const len = Math.hypot(zb - zt, yT - yB);
  const shape = new THREE.Shape();
  shape.moveTo(-wB / 2, 0);
  shape.lineTo(wB / 2, 0);
  shape.lineTo(wT / 2, len);
  shape.lineTo(-wT / 2, len);
  shape.closePath();
  const g = ni(new THREE.ShapeGeometry(shape));
  const ang = Math.atan2(zt - zb, yT - yB);
  g.rotateX(ang);
  // normal after rotateX: (0, -sin, cos)
  const nx = 0, ny = -Math.sin(ang), nz = Math.cos(ang);
  g.translate(nx * offset, ny * offset, nz * offset);
  if (facing < 0) g.rotateY(Math.PI);
  g.translate(0, yB, zB);
  return g;
}

/**
 * Semicircular wheel-arch notch for a side profile whose bottom edge runs rear -> front at
 * y = bottomY (the closing segment). Points go from the rear end of the arch to the front end.
 */
function archPts(axleZ: number, bottomY: number, r: number, segs = 9): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= segs; i++) {
    const th = Math.PI - (i / segs) * Math.PI;
    out.push([axleZ + r * Math.cos(th), bottomY + r * Math.sin(th)]);
  }
  return out;
}

function merge(list: THREE.BufferGeometry[]): THREE.BufferGeometry {
  if (list.length === 0) {
    // an empty but valid geometry
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([], 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute([], 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute([], 2));
    return g;
  }
  const g = mergeGeometries(list, false);
  if (!g) throw new Error('vehicle_mesh: mergeGeometries failed');
  for (const l of list) l.dispose();
  g.computeBoundingSphere();
  return g;
}

/** Single-material wheel: cylinder whose caps sample the rim face and whose tread samples the right half. */
function wheelGeometry(r: number, w: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, w, 22, 1, false);
  const uv = g.attributes.uv as THREE.BufferAttribute;
  const idx = g.index!;
  const done = new Uint8Array(uv.count);
  for (const gr of g.groups) {
    const cap = gr.materialIndex !== 0;
    for (let i = gr.start; i < gr.start + gr.count; i++) {
      const vi = idx.getX(i);
      if (done[vi]) continue;
      done[vi] = 1;
      const u = uv.getX(vi);
      const v = uv.getY(vi);
      uv.setXY(vi, cap ? u * 0.5 : 0.5 + u * 0.5, v);
    }
  }
  g.clearGroups();
  g.rotateZ(Math.PI / 2); // axis -> X
  return g;
}

// ------------------------------------------------------------ car builder ----

interface CarDesc {
  type: VehicleType;
  length: number;
  width: number;
  profile: Pt[];
  belt: number;
  roof: number;
  /** windscreen bottom (z,y) -> top (z,y) */
  ws: [number, number, number, number];
  /** rear window bottom (z,y) -> top (z,y) (z of top is closer to the front) */
  rw: [number, number, number, number];
  sideWindows: Pt[][];
  /** z of vertical door seams */
  doors: number[];
  frontAxle: number;
  rearAxle: number;
  wheelR: number;
  wheelW: number;
  headY: number;
  headH: number;
  headW: number;
  headX: number;
  tailY: number;
  tailH: number;
  tailW: number;
  tailX: number;
  bumperY: number;
  bumperH: number;
  grilleW: number;
  seats: SeatSlot[];
  doorZ: number;
  bonnet: { y: number; z: number };
  mirrorZ: number;
  sill: boolean;
  spoiler?: boolean;
  roofRails?: boolean;
  defaultColor: number | null;
}

function buildCar(d: CarDesc): VehicleTemplate {
  const M = getMats();
  const L = d.length, W = d.width, hw = W / 2;
  const body: THREE.BufferGeometry[] = [];
  const trim: THREE.BufferGeometry[] = [];
  const glass: THREE.BufferGeometry[] = [];
  const head: THREE.BufferGeometry[] = [];
  const tail: THREE.BufferGeometry[] = [];
  const extras: ExtraPart[] = [];

  // body shell with real wheel arches cut into the bottom edge of the profile
  const bottomY = d.profile[0][1];
  const archR = d.wheelR + 0.09;
  body.push(profileGeo([...d.profile, ...archPts(d.rearAxle, bottomY, archR), ...archPts(d.frontAxle, bottomY, archR)], W, 0.07));
  // inner wheel wells (dark filler between the two wheels so the arches read closed)
  const wellW = W - 2 * d.wheelW - 0.1;
  for (const z of [d.frontAxle, d.rearAxle]) trim.push(box(wellW, archR * 1.05, archR * 2, 0, bottomY + archR * 0.45, z));

  // glass
  const [wzB, wyB, wzT, wyT] = d.ws;
  glass.push(slantQuad(wzB, wyB, wzT, wyT, W - 0.26, W - 0.4, 1));
  const [rzB, ryB, rzT, ryT] = d.rw;
  glass.push(slantQuad(rzB, ryB, rzT, ryT, W - 0.3, W - 0.46, -1));
  for (const poly of d.sideWindows) glass.push(...sideQuads(poly, hw + 0.006));

  // trim: bumpers, grille, sills, door seams, handles, mirrors, exhaust
  trim.push(box(W + 0.02, d.bumperH, 0.16, 0, d.bumperY, L / 2 - 0.03));
  trim.push(box(W + 0.02, d.bumperH, 0.16, 0, d.bumperY, -L / 2 + 0.03));
  trim.push(box(d.grilleW, d.headH * 0.9, 0.03, 0, d.headY, L / 2 + 0.005));
  if (d.sill) {
    trim.push(box(0.05, 0.07, L * 0.6, hw - 0.005, d.profile[0][1] + 0.03, 0));
    trim.push(box(0.05, 0.07, L * 0.6, -hw + 0.005, d.profile[0][1] + 0.03, 0));
  }
  const seamH = d.belt - d.profile[0][1] - 0.1;
  for (const z of d.doors) {
    trim.push(box(0.016, seamH, 0.02, hw + 0.004, d.profile[0][1] + 0.08 + seamH / 2, z));
    trim.push(box(0.016, seamH, 0.02, -hw - 0.004, d.profile[0][1] + 0.08 + seamH / 2, z));
  }
  // door handles just behind each seam except the last one
  for (let i = 0; i < d.doors.length - 1; i++) {
    const z = d.doors[i] - 0.28;
    trim.push(box(0.025, 0.035, 0.16, hw + 0.012, d.belt - 0.13, z));
    trim.push(box(0.025, 0.035, 0.16, -hw - 0.012, d.belt - 0.13, z));
  }
  // mirrors (housing + stalk)
  for (const s of [1, -1] as const) {
    trim.push(box(0.17, 0.1, 0.07, s * (hw + 0.13), d.belt + 0.12, d.mirrorZ));
    trim.push(box(0.12, 0.025, 0.03, s * (hw + 0.06), d.belt + 0.08, d.mirrorZ));
  }
  // exhaust
  trim.push(cyl(0.035, 0.14, -hw + 0.4, d.profile[0][1] - 0.02, -L / 2 - 0.02, 'z', 8));
  // wipers
  trim.push(box(0.5, 0.015, 0.03, 0.25, wyB + 0.02, wzB + 0.03, 0, 0, 0));
  if (d.spoiler) {
    trim.push(box(W - 0.5, 0.03, 0.22, 0, d.profile[d.profile.length - 3][1] + 0.12, -L / 2 + 0.28));
    trim.push(box(0.04, 0.12, 0.12, hw - 0.45, d.profile[d.profile.length - 3][1] + 0.05, -L / 2 + 0.3));
    trim.push(box(0.04, 0.12, 0.12, -hw + 0.45, d.profile[d.profile.length - 3][1] + 0.05, -L / 2 + 0.3));
  }
  if (d.roofRails) {
    trim.push(box(0.05, 0.05, L * 0.45, hw - 0.2, d.roof + 0.03, -0.55));
    trim.push(box(0.05, 0.05, L * 0.45, -hw + 0.2, d.roof + 0.03, -0.55));
  }

  // lights
  head.push(box(d.headW, d.headH, 0.06, d.headX, d.headY, L / 2 + 0.01));
  head.push(box(d.headW, d.headH, 0.06, -d.headX, d.headY, L / 2 + 0.01));
  tail.push(box(d.tailW, d.tailH, 0.06, d.tailX, d.tailY, -L / 2 - 0.01));
  tail.push(box(d.tailW, d.tailH, 0.06, -d.tailX, d.tailY, -L / 2 - 0.01));

  // number plates (front + rear)
  extras.push({ geo: merge([texBox(0.44, 0.11, 0.012, 0, d.bumperY, L / 2 + 0.08, [4]), texBox(0.44, 0.11, 0.012, 0, d.bumperY, -L / 2 - 0.08, [5])]), mat: M.plate, kind: 'static', castShadow: false });

  // type specials
  if (d.type === 'taxi') {
    const y = d.roof + 0.1;
    extras.push({ geo: box(0.52, 0.17, 0.22, 0, y, 0.05), mat: M.cream, kind: 'static', castShadow: true });
    extras.push({ geo: merge([texBox(0.5, 0.15, 0.24, 0, y, 0.05, [4, 5])]), mat: M.taxiSign, kind: 'taxiSign', castShadow: false });
    const bandY = d.belt - 0.28;
    extras.push({
      geo: merge([texBox(0.012, 0.08, L * 0.62, hw + 0.006, bandY, -0.25, [0]), texBox(0.012, 0.08, L * 0.62, -hw - 0.006, bandY, -0.25, [1])]),
      mat: M.taxiCheck,
      kind: 'static',
      castShadow: false,
    });
  }
  if (d.type === 'police') {
    const y = d.roof + 0.05;
    trim.push(box(1.15, 0.08, 0.3, 0, y, -0.32));
    trim.push(box(0.06, 0.12, 0.28, 0, y + 0.08, -0.32));
    extras.push({ geo: box(0.5, 0.12, 0.26, 0.3, y + 0.08, -0.32), mat: M.lensRed, kind: 'lensRed', castShadow: false });
    extras.push({ geo: box(0.5, 0.12, 0.26, -0.3, y + 0.08, -0.32), mat: M.lensBlue, kind: 'lensBlue', castShadow: false });
    // door panels with "POLICE"
    const panelLen = d.doors[0] - d.doors[d.doors.length - 1];
    const panelZ = (d.doors[0] + d.doors[d.doors.length - 1]) / 2;
    const panelH = d.belt - d.profile[0][1] - 0.14;
    const panelY = d.profile[0][1] + 0.1 + panelH / 2;
    extras.push({
      geo: merge([texBox(0.012, panelH, panelLen, hw + 0.007, panelY, panelZ, [0]), texBox(0.012, panelH, panelLen, -hw - 0.007, panelY, panelZ, [1])]),
      mat: M.policeDoor,
      kind: 'static',
      castShadow: false,
    });
    // bonnet stripe: follows the bonnet slope
    const b0 = d.profile[2], b1 = d.profile[3]; // bonnet line points (front -> cabin)
    const slope = Math.atan2(b1[1] - b0[1], b0[0] - b1[0]);
    const zc = (b0[0] + b1[0]) / 2;
    const yc = (b0[1] + b1[1]) / 2 + 0.012;
    extras.push({ geo: box(0.9, 0.012, (b0[0] - b1[0]) * 0.8, 0, yc, zc, slope), mat: M.policeBlue, kind: 'static', castShadow: false });
    // boot stripe
    const t0 = d.profile[d.profile.length - 4];
    const t1 = d.profile[d.profile.length - 3];
    extras.push({ geo: box(0.9, 0.012, Math.abs(t0[0] - t1[0]) * 0.7, 0, (t0[1] + t1[1]) / 2 + 0.012, (t0[0] + t1[0]) / 2), mat: M.policeBlue, kind: 'static', castShadow: false });
  }

  const wheelX = hw - d.wheelW / 2 - 0.02;
  const wheels: WheelSlot[] = [
    { x: wheelX, z: d.frontAxle, steer: true },
    { x: -wheelX, z: d.frontAxle, steer: true },
    { x: wheelX, z: d.rearAxle, steer: false },
    { x: -wheelX, z: d.rearAxle, steer: false },
  ];
  const colR = hw;
  const n = Math.max(2, Math.ceil(L / (1.6 * colR)));
  const span = L / 2 - colR * 0.85;
  const colZ: number[] = [];
  for (let i = 0; i < n; i++) colZ.push(-span + (2 * span * i) / (n - 1));

  return {
    type: d.type,
    body: merge(body),
    trim: merge(trim),
    glass: merge(glass),
    head: merge(head),
    tail: merge(tail),
    extras,
    wheels,
    wheelRadius: d.wheelR,
    wheelGeo: wheelGeometry(d.wheelR, d.wheelW),
    seats: d.seats,
    doorZ: d.doorZ,
    bonnet: d.bonnet,
    wheelbase: d.frontAxle - d.rearAxle,
    colZ,
    colR,
    defaultColor: d.defaultColor,
    headlightPos: { x: d.headX, y: d.headY, z: L / 2 },
  };
}

// ------------------------------------------------------------ descriptors ----

function sedanDesc(type: VehicleType, defaultColor: number | null): CarDesc {
  return {
    type,
    length: 4.5,
    width: 1.9,
    profile: [
      [2.25, 0.34], [2.25, 0.78], [2.15, 0.86], [0.9, 0.94], [0.75, 0.97], [0.32, 1.42], [0.05, 1.47],
      [-0.7, 1.47], [-0.95, 1.44], [-1.5, 0.98], [-2.1, 0.94], [-2.25, 0.86], [-2.25, 0.34],
    ],
    belt: 0.96,
    roof: 1.47,
    ws: [0.75, 0.97, 0.32, 1.42],
    rw: [-1.5, 0.98, -0.95, 1.44],
    sideWindows: [
      [[0.7, 0.99], [0.34, 1.4], [-0.25, 1.4], [-0.25, 0.99]],
      [[-0.33, 0.99], [-0.33, 1.4], [-0.9, 1.4], [-1.4, 0.99]],
    ],
    doors: [0.74, -0.29, -1.45],
    frontAxle: 1.42,
    rearAxle: -1.38,
    wheelR: 0.33,
    wheelW: 0.22,
    headY: 0.62, headH: 0.17, headW: 0.42, headX: 0.63,
    tailY: 0.68, tailH: 0.15, tailW: 0.44, tailX: 0.62,
    bumperY: 0.42, bumperH: 0.16, grilleW: 0.72,
    seats: [
      { x: 0.38, y: 0.42, z: 0.22 },
      { x: -0.38, y: 0.42, z: 0.22 },
      { x: 0.38, y: 0.42, z: -0.62 },
      { x: -0.38, y: 0.42, z: -0.62 },
    ],
    doorZ: 0.22,
    bonnet: { y: 0.95, z: 1.45 },
    mirrorZ: 0.68,
    sill: true,
    defaultColor,
  };
}

function hatchDesc(): CarDesc {
  return {
    type: 'hatch',
    length: 3.9,
    width: 1.8,
    profile: [
      [1.95, 0.34], [1.95, 0.76], [1.85, 0.84], [0.8, 0.92], [0.62, 0.95], [0.17, 1.42], [-0.1, 1.47],
      [-1.2, 1.47], [-1.45, 1.42], [-1.85, 1.0], [-1.95, 0.9], [-1.95, 0.34],
    ],
    belt: 0.95,
    roof: 1.47,
    ws: [0.62, 0.95, 0.17, 1.42],
    rw: [-1.85, 1.0, -1.45, 1.42],
    sideWindows: [
      [[0.58, 0.97], [0.2, 1.4], [-0.35, 1.4], [-0.35, 0.97]],
      [[-0.43, 0.97], [-0.43, 1.4], [-1.2, 1.4], [-1.5, 0.97]],
    ],
    doors: [0.6, -0.39, -1.52],
    frontAxle: 1.2,
    rearAxle: -1.25,
    wheelR: 0.31,
    wheelW: 0.2,
    headY: 0.6, headH: 0.17, headW: 0.4, headX: 0.58,
    tailY: 0.66, tailH: 0.16, tailW: 0.36, tailX: 0.6,
    bumperY: 0.42, bumperH: 0.16, grilleW: 0.6,
    seats: [
      { x: 0.36, y: 0.42, z: 0.12 },
      { x: -0.36, y: 0.42, z: 0.12 },
      { x: 0.36, y: 0.42, z: -0.68 },
      { x: -0.36, y: 0.42, z: -0.68 },
    ],
    doorZ: 0.12,
    bonnet: { y: 0.93, z: 1.3 },
    mirrorZ: 0.55,
    sill: true,
    defaultColor: null,
  };
}

function sportsDesc(): CarDesc {
  return {
    type: 'sports',
    length: 4.4,
    width: 1.95,
    profile: [
      [2.2, 0.3], [2.2, 0.6], [2.12, 0.66], [0.5, 0.78], [0.3, 0.81], [-0.2, 1.22], [-0.45, 1.27],
      [-1.0, 1.27], [-1.3, 1.2], [-1.9, 0.88], [-2.15, 0.85], [-2.2, 0.72], [-2.2, 0.3],
    ],
    belt: 0.8,
    roof: 1.27,
    ws: [0.3, 0.81, -0.2, 1.22],
    rw: [-1.9, 0.88, -1.3, 1.2],
    sideWindows: [[[0.26, 0.83], [-0.18, 1.2], [-1.05, 1.2], [-1.38, 0.83]]],
    doors: [0.3, -1.42],
    frontAxle: 1.45,
    rearAxle: -1.4,
    wheelR: 0.34,
    wheelW: 0.27,
    headY: 0.5, headH: 0.1, headW: 0.5, headX: 0.62,
    tailY: 0.58, tailH: 0.11, tailW: 0.5, tailX: 0.6,
    bumperY: 0.38, bumperH: 0.14, grilleW: 0.9,
    seats: [
      { x: 0.4, y: 0.24, z: -0.4 },
      { x: -0.4, y: 0.24, z: -0.4 },
    ],
    doorZ: -0.3,
    bonnet: { y: 0.78, z: 1.3 },
    mirrorZ: 0.25,
    sill: true,
    spoiler: true,
    defaultColor: null,
  };
}

function suvDesc(): CarDesc {
  return {
    type: 'suv',
    length: 4.8,
    width: 2.0,
    profile: [
      [2.4, 0.42], [2.4, 1.0], [2.3, 1.08], [1.05, 1.15], [0.9, 1.19], [0.45, 1.8], [0.2, 1.86],
      [-2.0, 1.86], [-2.25, 1.8], [-2.4, 1.5], [-2.4, 0.42],
    ],
    belt: 1.18,
    roof: 1.86,
    ws: [0.9, 1.19, 0.45, 1.8],
    rw: [-2.4, 1.5, -2.25, 1.8],
    sideWindows: [
      [[0.85, 1.21], [0.5, 1.78], [-0.2, 1.78], [-0.2, 1.21]],
      [[-0.28, 1.21], [-0.28, 1.78], [-1.2, 1.78], [-1.2, 1.21]],
      [[-1.28, 1.21], [-1.28, 1.78], [-2.0, 1.78], [-2.08, 1.21]],
    ],
    doors: [0.88, -0.24, -1.24],
    frontAxle: 1.5,
    rearAxle: -1.45,
    wheelR: 0.4,
    wheelW: 0.27,
    headY: 0.8, headH: 0.2, headW: 0.42, headX: 0.68,
    tailY: 1.05, tailH: 0.34, tailW: 0.24, tailX: 0.78,
    bumperY: 0.52, bumperH: 0.2, grilleW: 0.8,
    seats: [
      { x: 0.4, y: 0.74, z: 0.3 },
      { x: -0.4, y: 0.74, z: 0.3 },
      { x: 0.4, y: 0.74, z: -0.6 },
      { x: -0.4, y: 0.74, z: -0.6 },
    ],
    doorZ: 0.3,
    bonnet: { y: 1.16, z: 1.6 },
    mirrorZ: 0.82,
    sill: true,
    roofRails: true,
    defaultColor: null,
  };
}

function vanDesc(): CarDesc {
  return {
    type: 'van',
    length: 5.2,
    width: 2.0,
    profile: [
      [2.6, 0.38], [2.6, 0.9], [2.55, 1.0], [1.75, 1.1], [1.6, 1.14], [1.15, 2.05], [0.95, 2.14],
      [-2.45, 2.16], [-2.6, 2.05], [-2.6, 0.38],
    ],
    belt: 1.14,
    roof: 2.16,
    ws: [1.6, 1.14, 1.15, 2.05],
    rw: [-2.6, 1.45, -2.58, 2.0],
    sideWindows: [[[1.55, 1.17], [1.18, 2.0], [0.35, 2.0], [0.35, 1.17]]],
    doors: [1.58, 0.3, -1.4],
    frontAxle: 1.65,
    rearAxle: -1.55,
    wheelR: 0.36,
    wheelW: 0.24,
    headY: 0.7, headH: 0.2, headW: 0.4, headX: 0.68,
    tailY: 1.1, tailH: 0.5, tailW: 0.16, tailX: 0.88,
    bumperY: 0.48, bumperH: 0.2, grilleW: 0.9,
    seats: [
      { x: 0.45, y: 0.95, z: 0.9 },
      { x: -0.45, y: 0.95, z: 0.9 },
    ],
    doorZ: 0.9,
    bonnet: { y: 1.12, z: 2.0 },
    mirrorZ: 1.5,
    sill: false,
    defaultColor: null,
  };
}

// ---------------------------------------------------------------- truck ----

function buildTruck(): VehicleTemplate {
  const M = getMats();
  const W = 2.5, hw = W / 2, L = 8;
  const body: THREE.BufferGeometry[] = [];
  const trim: THREE.BufferGeometry[] = [];
  const glass: THREE.BufferGeometry[] = [];
  const head: THREE.BufferGeometry[] = [];
  const tail: THREE.BufferGeometry[] = [];
  const extras: ExtraPart[] = [];

  // cab (with a front wheel arch)
  const cab: Pt[] = [[4.0, 0.55], [4.0, 1.18], [3.95, 1.32], [3.6, 1.48], [3.45, 1.56], [3.15, 2.9], [3.0, 2.98], [1.85, 2.98], [1.7, 2.9], [1.7, 0.55], ...archPts(2.9, 0.55, 0.62)];
  body.push(profileGeo(cab, W, 0.08));
  trim.push(box(W - 0.7, 0.66, 1.24, 0, 0.83, 2.9));
  // sleeper/deflector on the cab roof
  body.push(box(W - 0.4, 0.3, 1.0, 0, 3.1, 2.3));
  // cargo box
  extras.push({ geo: box(W, 2.3, 5.4, 0, 2.1, -1.35), mat: M.cargo, kind: 'static', castShadow: true });
  // cargo box detail: rear door seams + side rails
  trim.push(box(0.02, 2.1, 0.03, 0, 2.1, -4.06));
  trim.push(box(W + 0.02, 0.06, 0.04, 0, 0.98, -4.06));
  trim.push(box(0.04, 0.06, 5.3, hw + 0.01, 1.0, -1.35));
  trim.push(box(0.04, 0.06, 5.3, -hw - 0.01, 1.0, -1.35));
  // chassis + fuel tank + rear bumper bar
  trim.push(box(1.1, 0.45, 5.9, 0, 0.72, -1.2));
  trim.push(cyl(0.3, 1.1, hw - 0.35, 0.65, 0.4, 'z', 12));
  trim.push(box(W - 0.2, 0.12, 0.1, 0, 0.6, -4.0));
  // front bumper + grille
  trim.push(box(W + 0.04, 0.32, 0.18, 0, 0.72, 3.96));
  trim.push(box(1.3, 0.28, 0.03, 0, 1.03, 4.005));
  // mirrors (big, on arms)
  for (const s of [1, -1] as const) {
    trim.push(box(0.12, 0.42, 0.22, s * (hw + 0.22), 2.25, 3.4));
    trim.push(box(0.3, 0.03, 0.03, s * (hw + 0.1), 2.45, 3.4));
  }
  // door seams + steps
  trim.push(box(0.016, 1.9, 0.02, hw + 0.004, 1.7, 2.6));
  trim.push(box(0.016, 1.9, 0.02, -hw - 0.004, 1.7, 2.6));
  trim.push(box(0.35, 0.05, 0.6, hw - 0.1, 0.62, 2.9));
  trim.push(box(0.35, 0.05, 0.6, -hw + 0.1, 0.62, 2.9));
  // glass
  glass.push(slantQuad(3.45, 1.56, 3.15, 2.9, W - 0.3, W - 0.42, 1));
  glass.push(...sideQuads([[3.38, 1.62], [3.15, 2.82], [1.9, 2.82], [1.9, 1.62]], hw + 0.006));
  // lights
  head.push(box(0.5, 0.2, 0.06, 0.85, 1.03, 4.01));
  head.push(box(0.5, 0.2, 0.06, -0.85, 1.03, 4.01));
  tail.push(box(0.3, 0.3, 0.06, 1.0, 1.2, -4.08));
  tail.push(box(0.3, 0.3, 0.06, -1.0, 1.2, -4.08));
  extras.push({ geo: merge([texBox(0.44, 0.11, 0.012, 0, 0.72, 4.06, [4]), texBox(0.44, 0.11, 0.012, 0, 0.75, -4.09, [5])]), mat: M.plate, kind: 'static', castShadow: false });

  const wheelX = hw - 0.15 - 0.03;
  const wheels: WheelSlot[] = [
    { x: wheelX, z: 2.9, steer: true },
    { x: -wheelX, z: 2.9, steer: true },
    { x: wheelX, z: -2.1, steer: false },
    { x: -wheelX, z: -2.1, steer: false },
    { x: wheelX, z: -3.3, steer: false },
    { x: -wheelX, z: -3.3, steer: false },
  ];
  const colR = hw;
  const n = Math.ceil(L / (1.6 * colR));
  const span = L / 2 - colR * 0.85;
  const colZ: number[] = [];
  for (let i = 0; i < n; i++) colZ.push(-span + (2 * span * i) / (n - 1));
  return {
    type: 'truck',
    body: merge(body),
    trim: merge(trim),
    glass: merge(glass),
    head: merge(head),
    tail: merge(tail),
    extras,
    wheels,
    wheelRadius: 0.5,
    wheelGeo: wheelGeometry(0.5, 0.3),
    seats: [
      { x: 0.5, y: 1.45, z: 2.6 },
      { x: -0.5, y: 1.45, z: 2.6 },
    ],
    doorZ: 2.6,
    bonnet: { y: 1.6, z: 3.9 },
    wheelbase: 2.9 + 2.7,
    colZ,
    colR,
    defaultColor: null,
    headlightPos: { x: 0.85, y: 1.03, z: 4.0 },
  };
}

// ------------------------------------------------------------------ bus ----

function buildBus(): VehicleTemplate {
  const M = getMats();
  const W = 2.5, hw = W / 2, L = 11;
  const body: THREE.BufferGeometry[] = [];
  const trim: THREE.BufferGeometry[] = [];
  const glass: THREE.BufferGeometry[] = [];
  const head: THREE.BufferGeometry[] = [];
  const tail: THREE.BufferGeometry[] = [];
  const extras: ExtraPart[] = [];

  body.push(
    profileGeo(
      [[5.5, 0.42], [5.5, 2.6], [5.4, 3.05], [5.2, 3.2], [-5.2, 3.2], [-5.4, 3.05], [-5.5, 2.7], [-5.5, 0.42], ...archPts(-2.6, 0.42, 0.62), ...archPts(3.5, 0.42, 0.62)],
      W,
      0.1,
    ),
  );
  for (const z of [3.5, -2.6]) trim.push(box(W - 0.75, 0.66, 1.24, 0, 0.7, z));
  // roof AC unit + rear engine grille
  trim.push(box(1.6, 0.26, 2.2, 0, 3.32, -1.2));
  trim.push(box(2.0, 0.9, 0.03, 0, 1.0, -5.515));
  // side windows: a strip of panes with pillars; two doors on the right side
  const paneW = 1.4, gap = 0.14, zStart = 4.9;
  const doorPanes = new Set([1, 4]);
  for (let i = 0; i < 6; i++) {
    const z1 = zStart - i * (paneW + gap);
    const z0 = z1 - paneW;
    // left side: all normal panes
    glass.push(sideQuad(1, [[z1, 1.55], [z1, 2.65], [z0, 2.65], [z0, 1.55]], hw + 0.006));
    if (doorPanes.has(i)) {
      glass.push(sideQuad(-1, [[z1, 0.6], [z1, 2.65], [z0, 2.65], [z0, 0.6]], -hw - 0.006));
      trim.push(box(0.016, 2.1, 0.03, -hw - 0.012, 1.62, (z0 + z1) / 2));
      trim.push(box(0.016, 2.1, 0.03, -hw - 0.012, 1.62, z1 + 0.01));
      trim.push(box(0.016, 2.1, 0.03, -hw - 0.012, 1.62, z0 - 0.01));
    } else {
      glass.push(sideQuad(-1, [[z1, 1.55], [z1, 2.65], [z0, 2.65], [z0, 1.55]], -hw - 0.006));
    }
  }
  // belt line trim + skirts + bumpers
  trim.push(box(0.02, 0.06, 10.4, hw + 0.006, 1.5, 0));
  trim.push(box(0.02, 0.06, 10.4, -hw - 0.006, 1.5, 0));
  trim.push(box(W + 0.04, 0.3, 0.16, 0, 0.6, 5.46));
  trim.push(box(W + 0.04, 0.3, 0.16, 0, 0.6, -5.46));
  // mirrors (bus style: arms on the A-pillars)
  for (const s of [1, -1] as const) {
    trim.push(box(0.14, 0.45, 0.2, s * (hw + 0.3), 2.3, 5.2));
    trim.push(box(0.4, 0.03, 0.03, s * (hw + 0.12), 2.5, 5.2));
  }
  // windscreen + rear window + destination sign
  glass.push(slantQuad(5.5, 1.2, 5.42, 2.62, W - 0.24, W - 0.3, 1));
  glass.push(slantQuad(-5.5, 1.6, -5.45, 2.65, W - 0.5, W - 0.56, -1));
  extras.push({ geo: merge([texBox(1.9, 0.3, 0.03, 0, 2.86, 5.44, [4])]), mat: M.busSign, kind: 'static', castShadow: false });
  // lights
  head.push(box(0.5, 0.22, 0.06, 0.85, 0.98, 5.51));
  head.push(box(0.5, 0.22, 0.06, -0.85, 0.98, 5.51));
  tail.push(box(0.3, 0.55, 0.06, 0.95, 1.2, -5.52));
  tail.push(box(0.3, 0.55, 0.06, -0.95, 1.2, -5.52));
  extras.push({ geo: merge([texBox(0.44, 0.11, 0.012, 0, 0.6, 5.55, [4]), texBox(0.44, 0.11, 0.012, 0, 0.9, -5.55, [5])]), mat: M.plate, kind: 'static', castShadow: false });

  const wheelX = hw - 0.16 - 0.03;
  const wheels: WheelSlot[] = [
    { x: wheelX, z: 3.5, steer: true },
    { x: -wheelX, z: 3.5, steer: true },
    { x: wheelX, z: -2.6, steer: false },
    { x: -wheelX, z: -2.6, steer: false },
  ];
  const colR = hw;
  const n = Math.ceil(L / (1.6 * colR));
  const span = L / 2 - colR * 0.85;
  const colZ: number[] = [];
  for (let i = 0; i < n; i++) colZ.push(-span + (2 * span * i) / (n - 1));
  return {
    type: 'bus',
    body: merge(body),
    trim: merge(trim),
    glass: merge(glass),
    head: merge(head),
    tail: merge(tail),
    extras,
    wheels,
    wheelRadius: 0.5,
    wheelGeo: wheelGeometry(0.5, 0.32),
    seats: [
      { x: 0.6, y: 1.0, z: 4.3 },
      { x: -0.6, y: 1.0, z: 3.0 },
    ],
    doorZ: 4.2,
    bonnet: { y: 1.4, z: -4.9 },
    wheelbase: 6.1,
    colZ,
    colR,
    defaultColor: BUS_BLUE,
    headlightPos: { x: 0.85, y: 0.98, z: 5.5 },
  };
}

// ---------------------------------------------------------------- cache ----

const cache = new Map<VehicleType, VehicleTemplate>();

export function getVehicleTemplate(type: VehicleType): VehicleTemplate {
  let t = cache.get(type);
  if (t) return t;
  switch (type) {
    case 'sedan':
      t = buildCar(sedanDesc('sedan', null));
      break;
    case 'taxi':
      t = buildCar(sedanDesc('taxi', TAXI_YELLOW));
      break;
    case 'police':
      t = buildCar(sedanDesc('police', POLICE_WHITE));
      break;
    case 'hatch':
      t = buildCar(hatchDesc());
      break;
    case 'sports':
      t = buildCar(sportsDesc());
      break;
    case 'suv':
      t = buildCar(suvDesc());
      break;
    case 'van':
      t = buildCar(vanDesc());
      break;
    case 'truck':
      t = buildTruck();
      break;
    case 'bus':
      t = buildBus();
      break;
  }
  cache.set(type, t);
  return t;
}
